import {
  GreyGrey60,
  IconExclamationTriangle,
  CollapsibleSection,
  TextInputField,
  Tooltip,
  YellowYellow60,
  Box,
  Flex
} from '@sequencehq/core-components'
import Spinner from 'components/Loading'
import { CustomerContactsField } from '../components/CustomerContactsField'
import { CustomerFormFieldControls } from '../types'
import { useSectionExpansion } from '../useSectionExpansion'
import { fieldStyles, inputStyles } from './styles'

export type AccountInformationSectionProps = {
  /**
   * The legal-name field control. Its `isValidating`/`isUnique` flags drive the
   * duplicate-name spinner and warning; they reflect the hook's built-in check
   * unless `disableDuplicateNameCheck` was passed.
   */
  legalNameField: CustomerFormFieldControls['legalName']
  /** Renders the contacts under the company name when passed. */
  contactsField?: CustomerFormFieldControls['contacts']
  autoFocus?: boolean
  'data-testid'?: string
}

export const AccountInformationSection = ({
  legalNameField,
  contactsField,
  autoFocus,
  'data-testid': testId
}: AccountInformationSectionProps) => {
  const { isOpen, onToggle } = useSectionExpansion(
    [legalNameField, contactsField].some(field =>
      Boolean(field?.validationErrors.length)
    )
  )

  return (
    <CollapsibleSection
      title="Account information"
      isOpen={isOpen}
      onToggle={onToggle}
      data-testid={testId}
    >
      <Flex direction="column" gap="12px">
        <TextInputField
          data-testid={testId ? `${testId}.legalName` : undefined}
          label="Company name"
          value={legalNameField.value}
          onChange={e => legalNameField.onChange(e.target.value)}
          validationErrors={legalNameField.validationErrors}
          autoFocus={autoFocus}
          placeholder="Company name"
          {...inputStyles}
          styles={fieldStyles}
          suffix={
            legalNameField.isValidating ? (
              <Spinner height="16px" width="16px" color={GreyGrey60} />
            ) : (
              !legalNameField.isUnique && (
                <Tooltip
                  label="A customer with this name already exists"
                  placement="top"
                  isDisabled={legalNameField.isUnique}
                >
                  <Box
                    as="span"
                    data-testid={
                      testId ? `${testId}.duplicateWarning` : undefined
                    }
                  >
                    <IconExclamationTriangle color={YellowYellow60} />
                  </Box>
                </Tooltip>
              )
            )
          }
          suffixStyles={{
            pointerEvents: 'initial'
          }}
        />
        {contactsField && (
          <CustomerContactsField
            data-testid={testId ? `${testId}.contacts` : undefined}
            label="Contacts"
            value={contactsField.value}
            onChange={contactsField.onChange}
            validationErrors={contactsField.validationErrors}
          />
        )}
      </Flex>
    </CollapsibleSection>
  )
}
